/**
 * Loads the two deposit snapshots behind the Kautionen & Sicherheiten page.
 *
 * TWO SOURCES, TWO FATES
 * ----------------------
 * The tenant and landlord aggregates answer opposite questions and fail
 * independently, so they are kept in separate state with separate errors and
 * separate retries. A dead landlord endpoint must not blank the tenant panel and
 * its three KPIs, and vice versa: half a page that says which half is missing is
 * far more useful than an empty one.
 *
 * CURRENT STATE, NEVER A PERIOD
 * -----------------------------
 * Neither endpoint accepts a year or a month, and this hook must never be keyed
 * to one. `tenancies` and `unit` record what a deposit IS, not what it was — an
 * edit overwrites the only value that exists — so "how much was held in August?"
 * is a question the data cannot answer. The dependency arrays below therefore
 * carry only the retry tokens. There is deliberately no `selectedBusinessPeriod`,
 * no `year`/`month`, and no `new Date()`: the answer's date comes from the
 * backend, which stamps the Swiss business day (Regel 51).
 *
 * RACE SAFETY
 * -----------
 * Each effect captures a generation number in a ref and drops its result if a
 * newer request has started since. Without it, a slow first attempt could land
 * after a fast retry and put the stale failure back on screen.
 *
 * INERT UNTIL ASKED FOR
 * ---------------------
 * `AdminUebersichtPage` mounts all five panels at once, so an ungated hook would
 * fire both requests at every page load for a tab nobody opened. `enabled`
 * defaults to true, which is what `/admin/kautionen` wants — that page IS the
 * request. The company-overview tab passes its own active flag instead.
 *
 * Disabling clears the source rather than freezing it: these are current-state
 * answers, so a figure from the last visit must not stand as though it were
 * fresh while the reopened tab is still asking. Re-enabling therefore re-asks,
 * exactly once, which is the behaviour `useFinanceData` already has on this page.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { sanitizeClientErrorMessage } from "../../../api/adminData/core";
import {
  fetchAdminLandlordPlacedDeposits,
  fetchAdminTenantHeldDeposits,
} from "../../../api/adminData";

const TENANT_FALLBACK = "Mieterkautionen konnten nicht geladen werden.";
const LANDLORD_FALLBACK = "Sicherheitsleistungen konnten nicht geladen werden.";

/** One shared object, so `setState(EMPTY)` on an already-empty source is a no-op
 *  instead of a fresh literal that would re-render for nothing. */
const EMPTY = { data: null, error: "" };

/**
 * One source's lifecycle. `settled` rather than `loading` so a retry of a source
 * that had already answered shows its skeletons again instead of leaving stale
 * figures standing as though they were fresh.
 */
function useDepositSource(fetcher, fallbackMessage, enabled) {
  const [state, setState] = useState(EMPTY);
  const [settled, setSettled] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const generationRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      // The previous run's cleanup has already retired anything in flight, so
      // this only has to drop the answer it would have shown.
      setState(EMPTY);
      setSettled(false);
      return undefined;
    }

    const generation = generationRef.current + 1;
    generationRef.current = generation;
    setSettled(false);

    fetcher().then(
      (data) => {
        if (generationRef.current !== generation) return;
        setState({ data, error: "" });
        setSettled(true);
      },
      (e) => {
        if (generationRef.current !== generation) return;
        setState({
          data: null,
          error: sanitizeClientErrorMessage(e?.message, fallbackMessage),
        });
        setSettled(true);
      }
    );

    return () => {
      // Bumping the generation retires anything still in flight: its result can
      // no longer match and will be dropped on arrival.
      generationRef.current += 1;
    };
    // `fetcher` and `fallbackMessage` are module-level constants passed by the
    // caller; the gate and the token are the only things that may re-run this.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, reloadToken]);

  const reload = useCallback(() => setReloadToken((t) => t + 1), []);

  // A closed tab is not "still loading": nothing was asked for, so skeletons
  // would never resolve.
  return {
    data: state.data,
    error: state.error,
    loading: enabled && !settled,
    reload,
  };
}

export default function useDepositsData({ enabled = true } = {}) {
  const tenant = useDepositSource(
    fetchAdminTenantHeldDeposits,
    TENANT_FALLBACK,
    enabled
  );
  const landlord = useDepositSource(
    fetchAdminLandlordPlacedDeposits,
    LANDLORD_FALLBACK,
    enabled
  );

  return { tenant, landlord };
}
